import React, { useCallback, useEffect, useRef, useState } from 'react';

import { useIntl } from 'react-intl';

import { cssMap, cx } from '@atlaskit/css';
import ArrowRightIcon from '@atlaskit/icon/core/arrow-right';
import CheckMarkIcon from '@atlaskit/icon/core/check-mark';
import LinkExternalIcon from '@atlaskit/icon/core/link-external';
import FilledStarIcon from '@atlaskit/icon/core/star-starred';
import UnfilledStarIcon from '@atlaskit/icon/core/star-unstarred';
import { fg } from '@atlaskit/platform-feature-flags';
import { Box, Flex, Pressable, Text, type TextColor } from '@atlaskit/primitives/compiled';
import { AgentVerifiedIcon } from '@atlaskit/rovo-agent-components/agent-verified';
import { token } from '@atlaskit/tokens';
import Tooltip, { type TriggerProps } from '@atlaskit/tooltip';

import { ConditionalTooltip } from '../../conditional-tooltip';
import { LoadingState } from '../../loading-state';

import messages from './messages';

const styles = cssMap({
	menuItem: {
		width: '100%',
		display: 'flex',
		flexDirection: 'row',
		minHeight: '32px',
		justifyContent: 'space-between',
		alignItems: 'center',
		gap: token('space.075'),
		borderRadius: token('radius.medium'),
		cursor: 'pointer',
		backgroundColor: token('color.background.neutral.subtle'),
		'&:active': {
			backgroundColor: token('color.background.neutral.subtle.pressed'),
		},
	},
	menuItemActive: {
		backgroundColor: token('color.background.selected'),
		color: token('color.text.selected'),
	},
	menuItemHover: {
		'&:hover': {
			backgroundColor: token('color.background.neutral.subtle.hovered'),
		},
	},
	menuItemFocused: {
		backgroundColor: token('color.background.neutral.subtle.hovered'),
	},
	menuItemDisabled: {
		cursor: 'not-allowed',
		color: token('color.text.disabled'),
		opacity: token('opacity.disabled'),
	},
	menuItemContent: {
		width: '100%',
		display: 'flex',
		alignItems: 'center',
		gap: token('space.075'),
		flex: '1',
		minWidth: '0px',
	},
	menuItemIcon: {
		display: 'flex',
		width: '24px',
		height: '24px',
		alignItems: 'center',
		justifyContent: 'center',
	},
	menuItemLabelContainer: {
		minWidth: '0px',
		textAlign: 'left',
		overflow: 'hidden',
		flexShrink: 1,
	},
	labelAndVerifiedContainer: {
		flex: '1 1 auto',
		minWidth: '0px',
		overflow: 'hidden',
	},
	menuItemLabel: {
		display: 'block',
		width: '100%',
		whiteSpace: 'nowrap',
		textOverflow: 'ellipsis',
		overflow: 'hidden',
	},
	favoriteIconContainer: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		paddingTop: token('space.050'),
		paddingBottom: token('space.050'),
		paddingLeft: token('space.050'),
		paddingRight: token('space.050'),
		borderRadius: token('radius.small'),
		cursor: 'pointer',
		backgroundColor: 'transparent',
		'&:hover': {
			backgroundColor: token('color.background.neutral.hovered'),
		},
		'&:active': {
			backgroundColor: token('color.background.neutral.pressed'),
		},
	},
	// When the favourite button is kept mounted for keyboard operability but the
	// item is neither favourited nor hovered, hide it visually while leaving it
	// in the accessibility tree and focusable. Revealed again on keyboard focus.
	favoriteIconHidden: {
		opacity: 0,
		'&:focus-visible': {
			opacity: 1,
		},
	},
});

interface MenuItemProps {
	label: string;
	icon: React.ReactNode;
	onClick: () => void;
	isActive?: boolean; // specific to agent menu items
	isFocused?: boolean; // keyboard-driven virtual focus (hover style)
	isFavorite?: boolean; // specific to agent menu items
	isVerified?: boolean | null; // specific to agent menu items
	onFavoriteClick?: () => void; // specific to agent menu items - handler for clicking the favorite icon
	isLoading?: boolean;
	isDisabled?: boolean;
	tooltip?: string;
	interactionName?: string;
	isExternalLink?: boolean;
	isSubMenu?: boolean;
	labelColor?: TextColor;
	testId?: string;
	componentName?: string;
}

export const MenuItem = ({
	label,
	icon,
	isVerified,
	onClick,
	isActive,
	isFocused,
	isFavorite,
	onFavoriteClick,
	isLoading,
	isDisabled,
	tooltip,
	interactionName,
	isExternalLink,
	isSubMenu,
	labelColor,
	testId,
	componentName,
}: MenuItemProps): React.JSX.Element => {
	const { formatMessage } = useIntl();
	const [showExternalLinkIcon, setShowExternalLinkIcon] = useState(false);
	const [showSubMenuIcon, setShowSubMenuIcon] = useState(false);
	const [showFavoriteIcon, setShowFavoriteIcon] = useState(false);
	const [showTooltip, setShowTooltip] = useState(false);
	const textRef = useRef<HTMLElement>(null);
	const containerRef = useRef<HTMLElement>(null);

	useEffect(() => {
		const checkOverflow = () => {
			if (textRef.current && containerRef.current) {
				const isOverflowing = textRef.current.scrollWidth > containerRef.current.clientWidth;
				setShowTooltip(isOverflowing);
			}
		};

		checkOverflow();

		const resizeObserver = new ResizeObserver(checkOverflow);
		if (containerRef.current) {
			resizeObserver.observe(containerRef.current);
		}

		return () => resizeObserver.disconnect();
	}, [label]);

	const handleOnMouseEnter = useCallback(() => {
		if (isExternalLink) {
			setShowExternalLinkIcon(true);
		}
		if (isSubMenu) {
			setShowSubMenuIcon(true);
		}
		if (onFavoriteClick !== undefined) {
			setShowFavoriteIcon(true);
		}
	}, [isExternalLink, isSubMenu, onFavoriteClick]);

	const handleOnMouseLeave = useCallback(() => {
		if (isExternalLink) {
			setShowExternalLinkIcon(false);
		}
		if (isSubMenu) {
			setShowSubMenuIcon(false);
		}
		if (onFavoriteClick !== undefined) {
			setShowFavoriteIcon(false);
		}
	}, [isExternalLink, isSubMenu, onFavoriteClick]);

	const handleFavoriteClick = useCallback(
		(e: React.MouseEvent) => {
			e.stopPropagation();
			onFavoriteClick?.();
		},
		[onFavoriteClick],
	);

	const renderMenuItem = (tooltipProps: Partial<TriggerProps> = {}) => (
		<Pressable
			{...tooltipProps}
			xcss={cx(
				styles.menuItem,
				isActive && styles.menuItemActive,
				// if is focus is defined combobox is controlling hover styles
				isFocused === undefined && styles.menuItemHover,
				isFocused && styles.menuItemFocused,
				isDisabled && styles.menuItemDisabled,
			)}
			onClick={isDisabled ? undefined : onClick}
			onMouseEnter={handleOnMouseEnter}
			onMouseLeave={handleOnMouseLeave}
			aria-disabled={isDisabled}
			testId={testId || 'menu-item-container'}
			interactionName={interactionName}
			componentName={componentName}
		>
			<Box xcss={styles.menuItemContent} testId="menu-item-content">
				<Box xcss={styles.menuItemIcon}>
					{isLoading ? (
						<LoadingState size="small" testId="loading-state" />
					) : isActive && React.isValidElement(icon) ? (
						React.cloneElement(icon, { color: token('color.icon.selected') })
					) : (
						icon
					)}
				</Box>
				<Flex alignItems="center" gap="space.075" xcss={styles.labelAndVerifiedContainer}>
					<Box xcss={styles.menuItemLabelContainer} ref={containerRef}>
						<ConditionalTooltip withTooltip={showTooltip && !tooltip} content={label}>
							<Box xcss={styles.menuItemLabel} ref={textRef}>
								<Text
									color={
										labelColor ? labelColor : isActive ? 'color.text.selected' : 'color.text.subtle'
									}
									testId="menu-item-label"
								>
									{label}
								</Text>
							</Box>
						</ConditionalTooltip>
					</Box>
					{isVerified && fg('rovo_agents_agent_verification') && <AgentVerifiedIcon />}
				</Flex>
				{isExternalLink && showExternalLinkIcon && (
					<LinkExternalIcon label="link-external" color={token('color.icon.subtle')} />
				)}
				{isSubMenu && showSubMenuIcon && (
					<ArrowRightIcon label="" color={token('color.icon.subtle')} />
				)}
			</Box>
			{isFavorite !== undefined && onFavoriteClick && (
				<Pressable
					xcss={cx(
						styles.favoriteIconContainer,
						!isFavorite && !showFavoriteIcon && styles.favoriteIconHidden,
					)}
					onClick={handleFavoriteClick}
					testId="favorite-icon-container"
					aria-label={
						isFavorite
							? formatMessage(messages.removeFromFavouritesLabel, { agentName: label })
							: formatMessage(messages.addToFavouritesLabel, { agentName: label })
					}
				>
					{isFavorite ? (
						<FilledStarIcon label="" color={token('color.icon.warning')} />
					) : (
						<UnfilledStarIcon label="" color={token('color.icon.subtle')} />
					)}
				</Pressable>
			)}
			{isActive && <CheckMarkIcon label="checkmark" />}
		</Pressable>
	);

	return tooltip ? <Tooltip content={tooltip}>{renderMenuItem}</Tooltip> : renderMenuItem();
};
